import { request }                      from "./http";
import type { ClientRecord, SiteRecord } from "./types";

export type { ClientRecord, SiteRecord };
export type { SiteData } from "./types";

// ── Cache ────────────────────────────────────────────────────────────────────
// Une seule requête en vol pour les GETs ; résultat (promesse + valeur résolue)
// conservé jusqu'à mutation. `getCachedClients()`/`getCachedSites()` exposent la
// valeur déjà résolue de façon synchrone (null tant qu'aucun fetch n'a abouti) —
// utilisé par siteCache.ts pour dériver ses index sans dupliquer ce cache.

let _clients:     Promise<ClientRecord[]> | null = null;
let _sites:       Promise<SiteRecord[]>   | null = null;
let _clientsData: ClientRecord[]          | null = null;
let _sitesData:   SiteRecord[]            | null = null;

// Le cache est ici, en mémoire : une invalidation doit toujours aboutir à un vrai aller-retour
// serveur, jamais à une réponse servie par le cache HTTP du navigateur (sinon une modification
// faite depuis un autre onglet/poste reste invisible jusqu'au rechargement de la page).
const NO_HTTP_CACHE: RequestInit = { cache: "no-store" };

export function invalidateClients() { _clients = null; _clientsData = null; }
export function invalidateSites()   { _sites   = null; _sitesData   = null; }

export function getCachedClients(): ClientRecord[] | null { return _clientsData; }
export function getCachedSites():   SiteRecord[]   | null { return _sitesData; }

// ── Clients ───────────────────────────────────────────────────────────────────

export function listClients(): Promise<ClientRecord[]> {
  if (!_clients) {
    _clients = request<ClientRecord[]>("/clients", NO_HTTP_CACHE)
      .then(data => { _clientsData = data; return data; })
      .catch(err => { _clients = null; throw err; });
  }
  return _clients;
}

export async function upsertClient(
  client: Omit<ClientRecord, "createdAt" | "updatedAt"> & { id?: string },
): Promise<ClientRecord> {
  const result = await request<ClientRecord>("/clients", {
    method: "POST",
    body:   JSON.stringify(client),
  });
  invalidateClients();
  return result;
}

export async function deleteClient(id: string): Promise<void> {
  await request<{ ok: boolean }>(`/clients/${id}`, { method: "DELETE" });
  invalidateClients();
  invalidateSites(); // les sites du client sont aussi supprimés côté serveur
}

// ── Sites ─────────────────────────────────────────────────────────────────────

export function listSites(): Promise<SiteRecord[]> {
  if (!_sites) {
    _sites = request<SiteRecord[]>("/sites", NO_HTTP_CACHE)
      .then(data => { _sitesData = data; return data; })
      .catch(err => { _sites = null; throw err; });
  }
  return _sites;
}

export async function upsertSite(
  site: Omit<SiteRecord, "createdAt" | "updatedAt"> & { id?: string },
): Promise<SiteRecord> {
  const result = await request<SiteRecord>("/sites", {
    method: "POST",
    body:   JSON.stringify(site),
  });
  invalidateSites();
  return result;
}

export async function deleteSite(id: string): Promise<void> {
  await request<{ ok: boolean }>(`/sites/${id}`, { method: "DELETE" });
  invalidateSites();
}

/** Regroupe des sites par client (clé = clientId) — sites dans l'ordre reçu, aucun tri imposé (à
 * la charge de l'appelant si nécessaire). Sites sans clientId ignorés (absents de tout groupe). */
export function groupSitesByClient<T extends { clientId?: string }>(sites: T[]): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const s of sites) {
    if (!s.clientId) continue;
    if (!map.has(s.clientId)) map.set(s.clientId, []);
    map.get(s.clientId)!.push(s);
  }
  return map;
}
